import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { INK, INK_2, INK_3, LINE, BURGUNDY, BG } from "../Bureau/bureauTokens";

const JOURS_COURT = ["L", "M", "M", "J", "V", "S", "D"];
const MOIS_LABEL = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Mini-calendrier mensuel navigable — fidèle à MyALFA Worksync Test V2/
// temps-rh.jsx : pastille sous les jours ayant des saisies, cadenas sur
// les jours clôturés, jours futurs non cliquables. `entryDates`/
// `closedDates` sont des Set d'ISO fournis par FeuilleTemps.js (une seule
// source de vérité, pas de fetch propre à ce composant).
function MiniCalendar({ dayISO, todayISO, entryDates, closedDates, onPick }) {
  const [view, setView] = useState(() => new Date(dayISO));

  useEffect(() => {
    setView(new Date(dayISO));
  }, [dayISO]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const first = new Date(year, month, 1);
  const leading = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((leading + daysInMonth) / 7) * 7;

  const cells = Array.from({ length: totalCells }, (_, i) => {
    const dayNum = i - leading + 1;
    if (dayNum < 1 || dayNum > daysInMonth) return null;
    return new Date(year, month, dayNum);
  });

  const shiftMonth = (n) => setView(new Date(year, month + n, 1));

  return (
    <div className="p-2.5" style={{ width: 240 }}>
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => shiftMonth(-1)} className="flex items-center justify-center" style={{ width: 24, height: 24, borderRadius: 6, color: INK_2 }}>
          <ChevronLeft size={13} />
        </button>
        <span className="text-[11px] font-semibold" style={{ color: INK }}>
          {MOIS_LABEL[month]} {year}
        </span>
        <button onClick={() => shiftMonth(1)} className="flex items-center justify-center" style={{ width: 24, height: 24, borderRadius: 6, color: INK_2 }}>
          <ChevronRight size={13} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {JOURS_COURT.map((l, i) => (
          <div key={i} className="text-center text-[9.5px] font-semibold" style={{ color: INK_3 }}>
            {l}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const di = isoOf(d);
          const isSel = di === dayISO;
          const isToday = di === todayISO;
          const isClosed = closedDates.has(di);
          const isFuture = di > todayISO;
          const hasEntries = entryDates.has(di);
          return (
            <button
              key={i}
              onClick={() => !isFuture && onPick(di)}
              disabled={isFuture}
              className="relative flex flex-col items-center justify-center text-[10.5px]"
              style={{
                height: 26,
                borderRadius: 6,
                cursor: isFuture ? "default" : "pointer",
                background: isSel ? BURGUNDY : isToday ? BG : "transparent",
                color: isSel ? "#fff" : isFuture ? INK_3 : INK,
                opacity: isFuture ? 0.4 : 1,
                fontWeight: isToday && !isSel ? 700 : 400,
              }}
            >
              {d.getDate()}
              {isClosed && <Lock size={7} style={{ position: "absolute", top: 1, right: 2, opacity: 0.7 }} />}
              {hasEntries && !isClosed && (
                <span
                  className="absolute"
                  style={{ bottom: 2, width: 3, height: 3, borderRadius: "50%", background: isSel ? "#fff" : BURGUNDY }}
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-2 pt-2" style={{ borderTop: `1px solid ${LINE}` }}>
        <span className="flex items-center gap-1 text-[9.5px]" style={{ color: INK_3 }}>
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: BURGUNDY }} /> Saisie
        </span>
        <span className="flex items-center gap-1 text-[9.5px]" style={{ color: INK_3 }}>
          <Lock size={9} /> Clôturée
        </span>
      </div>
    </div>
  );
}

export default MiniCalendar;
